import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cases } from '../data/mockData';
import { ArrowLeft, Award, Upload } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const CaseDetail = () => {
  const { slug } = useParams();
  const { language, paths } = useLanguage();
  const t = translations[language];
  const caseItem = cases.find((c) => c.slug === slug);

  if (!caseItem) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-black mb-4">Case Not Found</h1>
          <Link to={paths.cases} className="text-[#ff5200] hover:underline">
            {t.cases.backToCases}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[70vh] overflow-hidden">
        <img
          src={caseItem.image}
          alt={caseItem.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex items-end">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-12 w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block bg-[#ff5200] text-white text-sm font-semibold px-4 py-2 rounded-full mb-4">
                {caseItem.category}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-white">
                {caseItem.title}
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Back Button */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
        <Link
          to={paths.cases}
          className="inline-flex items-center gap-2 text-[#ff5200] hover:text-[#e64a00] font-semibold transition-colors"
        >
          <ArrowLeft size={20} />
          {t.cases.backToCases}
        </Link>
      </div>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-20">
        {/* Brief */}
        {caseItem.content.brief && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Brief</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {caseItem.content.brief}
            </p>
          </motion.div>
        )}

        {/* Strategy */}
        {caseItem.content.strategy && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Strategy</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {caseItem.content.strategy}
            </p>
          </motion.div>
        )}

        {/* Execution */}
        {caseItem.content.execution && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Execution</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {caseItem.content.execution}
            </p>
          </motion.div>
        )}

        {/* Business Impact */}
        {caseItem.content.businessImpact && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Business Impact</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {caseItem.content.businessImpact}
            </p>
          </motion.div>
        )}

        {/* Awards */}
        {caseItem.content.awards && caseItem.content.awards.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gray-50 p-8 rounded-lg"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6 flex items-center gap-3">
              <Award className="text-[#ff5200]" size={32} />
              Awards
            </h2>
            <ul className="space-y-3">
              {caseItem.content.awards.map((award, index) => (
                <li
                  key={index}
                  className="flex items-start gap-3 text-lg text-gray-700"
                >
                  <span className="text-[#ff5200] mt-1">•</span>
                  <span>{award}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </section>

      {/* Project Gallery Section - Only show if there are photos */}
      {caseItem.gallery && caseItem.gallery.length > 0 && (
        <section className="py-20 px-6 lg:px-12 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            {/* Gallery Grid - Show only actual images */}
            <div className="grid md:grid-cols-2 gap-6">
              {caseItem.gallery.map((imageUrl, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative aspect-[3/2] bg-white rounded-lg overflow-hidden border-2 border-gray-200 hover:border-[#ff5200] transition-colors group"
                >
                  <img
                    src={imageUrl}
                    alt={`${caseItem.title} gallery ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold mb-8"
          >
            {t.cases.interestedWorking}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to={paths.contact}
              className="inline-block bg-[#ff5200] text-white px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#e64a00] transition-colors duration-300"
            >
              {t.home.getInTouch}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseDetail;